/**
 * Capture on open: a completed form locked without a snapshot gets one the first time someone
 * opens it (#6413).
 *
 * The rendering happens in BnSnapshotCaptureHost, mounted once in App.vue, which registers itself
 * here. ensureFormSnapshots renders each missing snapshot from the same print data the logs print
 * from, uploads it marked as captured after lock, and returns the snapshot ids for the caller to
 * open. A null result tells the caller to open the live form as it would without a snapshot.
 */

import { fetchFormPrintData } from '@/components/questionnaires/form/print/formPrintData';
import { captureSnapshot, uploadSnapshot, resolveSnapshotUrls, fetchSnapshotHtml, resolveQrId, SNAPSHOT_KIND_SYSTEM, SNAPSHOT_CAPTURE_SYSTEM } from '@/common/formSnapshot';
import { useSnapshotGate } from '@/composables/useSnapshotGate';
import { $log } from '@/common/api/log.service';
import { LOG_ERROR } from '@/common/log/log';

let captureHost = null;

/** @param {{ render: (printData: object) => Promise<string>, setProgress: (message: string|null) => void }} host */
export function registerSnapshotCaptureHost(host) {
    captureHost = host;
}

export function unregisterSnapshotCaptureHost(host) {
    if (captureHost === host) captureHost = null;
}

const progressMessage = (index, total) => (total > 1 ? `Processing snapshot ${index + 1} of ${total}` : 'Preparing snapshot…');

// Render a response's snapshot through the host, from the same print data the logs print from.
// A group note's response renders as its participant snapshot (group section + that participant).
const renderSnapshotHtml = async (host, qrId, canonical, groupNote) => {
    const [entry] = await fetchFormPrintData([{ qrId, canonical }]);
    if (!entry) throw new Error('Print data not found');
    const printData = { ...entry, snapshotMode: true, groupItemFilter: groupNote ? 'participant' : '' };
    const { html } = await captureSnapshot({
        getSnapshotHtml: async () => {
            const rendered = await host.render(printData);
            if (typeof rendered !== 'string' || !rendered.trim()) throw new Error('Snapshot render was empty');
            return rendered;
        },
    });
    return html;
};

const captureOne = async (host, qr, groupNote) => {
    const html = await renderSnapshotHtml(host, qr.id, qr.questionnaire, groupNote);
    const { documentReferenceId } = await uploadSnapshot(qr.id, html, groupNote ? 'participant' : null, { afterLock: true });
    if (!documentReferenceId) throw new Error('Snapshot upload returned no id');
    return documentReferenceId;
};

/**
 * Make sure every completed response in `qrs` has a snapshot, capturing the missing ones.
 *
 * @param {object[]} qrs QuestionnaireResponses behind the open (one form, or a group note's participants)
 * @param {{ groupNote?: boolean }} [options] groupNote captures each as a participant snapshot
 * @returns {Promise<string[]|null>} the completed responses' DocumentReference ids in input order,
 *   or null when capture is unavailable or anything failed
 */
export async function ensureFormSnapshots(qrs, { groupNote = false } = {}) {
    const host = captureHost;
    const completed = (qrs || []).filter((qr) => qr?.status === 'completed');
    if (!useSnapshotGate().isSnapshotsEnabled.value || !host || !completed.length) return null;

    const missing = completed.filter((qr) => !qr.formPdfSnapshot?.id);
    const captured = new Map();
    let current = null;
    try {
        for (const [index, qr] of missing.entries()) {
            current = qr;
            host.setProgress(progressMessage(index, missing.length));
            // One capture at a time: they share the host's single renderer.
            // eslint-disable-next-line no-await-in-loop
            captured.set(qr.id, await captureOne(host, qr, groupNote));
        }
    } catch (error) {
        $log.log(LOG_ERROR, 'Form snapshot capture on open failed', `QuestionnaireResponse ${current?.id} (of ${missing.map((qr) => qr.id).join(', ')}): ${error?.message || error}`);
        return null;
    } finally {
        host.setProgress(null);
    }

    return completed.map((qr) => qr.formPdfSnapshot?.id || captured.get(qr.id));
}

// ── Review repair (#6762) ─────────────────────────────────────────────────────
// From 2026-08-19 the treatment-plan family's print view printed each review's header line and
// dropped its narrative, and every snapshot captured since — at lock or on open — stored that.
// Those records carry a fingerprint: a review block with no narrative, or a review under the
// Initial treatment plan, which records none. Every place that shows or prints a stored snapshot
// loads it through loadSnapshotHtml, which rebuilds a fingerprinted one once from the response and
// replaces it in place. The server re-checks the stored copy before it overwrites anything.

const hasText = (elements) => elements.some((element) => (element.textContent || '').trim() !== '');

/**
 * Whether stored snapshot HTML carries the broken review renderer's output: a review block with
 * no narrative beneath its header, or any review under the Initial treatment plan.
 *
 * @param {string|null} html
 * @returns {boolean}
 */
export function snapshotNeedsReviewRepair(html) {
    if (typeof html !== 'string' || !html.trim()) return false;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const reviewWithoutNarrative = (selector, bodySelector) => Array.from(doc.querySelectorAll(selector)).some((review) => !hasText(Array.from(review.querySelectorAll(bodySelector))));
    if (reviewWithoutNarrative('.b-review', '.b-review-val') || reviewWithoutNarrative('.tp-review', '.tp-rev-body')) return true;
    return doc.querySelector('.w-initialTreatmentPlan .b-review, .w-initialTreatmentPlan .tp-review') !== null;
}

// One repair attempt per snapshot per page session, so a render that is somehow still stale (an
// old bundle, a server that disagrees) never loops.
const repairAttempted = new Set();

// The group-note kind a snapshot's DocumentReference carries ('participant' or 'combined'), or null.
const snapshotKind = (docRef) => {
    for (const category of docRef?.category || []) {
        const coding = (category?.coding || []).find((c) => c?.system === SNAPSHOT_KIND_SYSTEM && (c?.code === 'participant' || c?.code === 'combined'));
        if (coding) return coding.code;
    }
    return null;
};

// The DocumentReference as the server leaves it after a repair: marked as captured after lock and
// dated now, so the view labels it "Recorded <date>, after completion" without reading it again.
const markedAfterLock = (docRef) => {
    if (!docRef) return docRef;
    const category = (docRef.category || []).filter((entry) => !(entry?.coding || []).some((c) => c?.system === SNAPSHOT_CAPTURE_SYSTEM));
    category.push({ coding: [{ system: SNAPSHOT_CAPTURE_SYSTEM, code: 'after-lock' }] });
    return { ...docRef, category, date: new Date().toISOString() };
};

// A stored snapshot's URLs and HTML, read the way every caller read them before (#6762). With a
// fallback, a failed re-read answers with it rather than rejecting.
const readStored = async (documentReferenceId, fallback = null) => {
    try {
        const urls = await resolveSnapshotUrls(documentReferenceId);
        return { urls, html: await fetchSnapshotHtml(urls?.htmlUrl) };
    } catch (error) {
        if (!fallback) throw error;
        return fallback;
    }
};

/**
 * Load a stored snapshot's URLs and HTML, rebuilding it first when it carries the broken review
 * renderer's fingerprint (see snapshotNeedsReviewRepair). A legacy 'combined' group snapshot is
 * never rebuilt here; it is returned as stored and left for the automated backfill (#6413). When
 * the server declines the repair (snapshot_current, snapshot_combined) the stored copy is returned.
 * Never rejects on a failed repair: a render failure returns the stored HTML, an upload failure the
 * freshly rendered HTML.
 *
 * @param {string} documentReferenceId
 * @returns {Promise<{ urls: object|null, html: string }>}
 */
export async function loadSnapshotHtml(documentReferenceId) {
    const { urls, html } = await readStored(documentReferenceId);

    const docRef = urls?.docRef || null;
    const kind = snapshotKind(docRef);
    // A legacy 'combined' group snapshot (group + every participant, captured before #4064 split
    // them) covers more than any one response renders today. Rebuilding it as a participant
    // snapshot would narrow what the signed record covers, so it is left as stored for the
    // automated backfill (#6413); the server refuses to repair one too.
    const host = captureHost;
    if (kind === 'combined' || !snapshotNeedsReviewRepair(html) || !host || !useSnapshotGate().isSnapshotsEnabled.value || repairAttempted.has(documentReferenceId)) {
        return { urls, html };
    }
    repairAttempted.add(documentReferenceId);

    const qrId = resolveQrId(docRef);
    const groupNote = kind === 'participant';
    const context = `DocumentReference ${documentReferenceId}, QuestionnaireResponse ${qrId}`;

    host.setProgress('Preparing snapshot…');
    try {
        let rendered;
        try {
            if (!qrId) throw new Error('Snapshot names no response');
            rendered = await renderSnapshotHtml(host, qrId, undefined, groupNote);
        } catch (error) {
            $log.log(LOG_ERROR, 'Form snapshot review repair render failed', `${context}: ${error?.message || error}`);
            return { urls, html };
        }

        let settled;
        try {
            ({ settled } = await uploadSnapshot(qrId, rendered, groupNote ? 'participant' : null, { repair: true }));
        } catch (error) {
            $log.log(LOG_ERROR, 'Form snapshot review repair upload failed', `${context}: ${error?.message || error}`);
            return { urls, html: rendered };
        }

        // The server wrote nothing (the stored copy is already current, or is a legacy combined
        // snapshot): show what it holds, not this render.
        if (settled) return await readStored(documentReferenceId, { urls, html });

        return { urls: urls ? { ...urls, docRef: markedAfterLock(urls.docRef) } : urls, html: rendered };
    } finally {
        host.setProgress(null);
    }
}
